import { isToday as isTodayDateFns } from 'date-fns';
import { format, formatInTimeZone, toZonedTime } from 'date-fns-tz';

import { REPUBS } from '@cx-modules/constants';
import { type Repub } from '@cx-modules/types';

/**
 * @param {string} dateString - ISO Date string
 * @returns {boolean}
 */
export const isToday = (dateString: string): boolean => {
  return Boolean(dateString) && isTodayDateFns(new Date(dateString));
};

/**
 * Returns a date in a new full format
 *
 * @param {string} dateIsoString - Date string as DD/MM/YYYY (en-GB).
 * @returns {string} Formatted date.
 *
 * @example
 * // returns 'Wednesday 3rd April 2025'
 * getFullDate('03/04/2025T00:00:00Z');
 */
export const getFullDate = (dateIsoString: string): string => {
  if (!dateIsoString) {
    return '';
  }

  return format(new Date(dateIsoString), 'EEEE do MMMM YYYY', {
    timeZone: 'America/New_York',
  });
};
/**
 * Returns a date in a new short format
 *
 * @param {string} dateIsoString - Date string as ISO string.
 * @returns {string} Formatted date.
 *
 * @example
 * // returns '2nd April'
 * getShortDate('2025-04-01T21:00:00.000Z');
 */
export const getShortDate = (dateIsoString: string): string => {
  if (!dateIsoString) {
    return '';
  }

  if (isToday(dateIsoString)) {
    return 'Today';
  }

  return format(new Date(dateIsoString), 'do MMMM', {
    timeZone: 'America/New_York',
  });
};

const halfTimeStartMin = 45;
const halfTimeDuration = 15;
export const getInPlayTime = (dateString: string): string => {
  if (!dateString) return '';

  const timeDiff = Math.abs(new Date().getTime() - new Date(dateString).getTime());

  const matchElapsedMins = Math.floor(timeDiff / 1000 / 60);

  if (matchElapsedMins <= halfTimeStartMin) {
    return `${matchElapsedMins}'`;
  }

  if (matchElapsedMins > halfTimeStartMin + halfTimeDuration) {
    return `${matchElapsedMins - halfTimeDuration}'`;
  }

  return `${halfTimeStartMin}'`;
};

/**
 * Returns a date in a new full format
 *
 * @param {string} dateIsoString - Date string as DD/MM/YYYY (en-GB).
 * @returns {string} Formatted date.
 *
 * @example
 * // returns 'Wednesday, 2nd April'
 * getFullDate('2025-04-01T21:00:00.000Z');
 */
export const getDateForTitle = (dateIsoString: string): string => {
  if (!dateIsoString) {
    return '';
  }

  return format(new Date(dateIsoString), 'EEEE, do MMMM', {
    timeZone: 'America/New_York',
  });
};

export const getGroupTitle = ({
  dateIsoString,
  dateTimeFormat,
  repub,
  timeZone,
}: {
  dateIsoString: string;
  dateTimeFormat: string;
  repub: Repub;
  timeZone?: string;
}) => {
  const tz = timeZone ?? REPUBS[repub].dateProperties.timezone;
  const zonedDate = toZonedTime(new Date(dateIsoString), tz);
  const isToday = isTodayDateFns(zonedDate);

  if (isToday) {
    return REPUBS[repub].dateProperties.today;
  }

  return formatInTimeZone(dateIsoString, tz, dateTimeFormat, {
    locale: REPUBS[repub].dateProperties.locale,
  });
};

export const getStartTime = (dateString: string, repub: Repub, timeZone?: string) => {
  if (!dateString) {
    return '';
  }

  const tz = timeZone ?? REPUBS[repub].dateProperties.timezone;

  return formatInTimeZone(dateString, tz, REPUBS[repub].dateProperties.format.time, {
    locale: REPUBS[repub].dateProperties.locale,
  });
};

/**
 * Returns a normalized date key for grouping matches by day
 * Converts ISO timestamp to YYYY-MM-DD format in the specified timezone
 *
 * @param {string} dateIsoString - ISO timestamp (e.g., "2030-01-02T23:00:00Z")
 * @param {string} timeZone - IANA timezone (e.g., "America/New_York")
 * @returns {string} Normalized date key (e.g., "2030-01-02")
 *
 * @example
 * getDateGroupKey('2030-01-02T23:00:00Z', 'America/New_York') // "2030-01-02"
 * getDateGroupKey('2030-01-03T04:00:00Z', 'America/New_York') // "2030-01-02" (still Jan 2 in ET)
 */
export const getDateGroupKey = (dateIsoString: string, timeZone: string): string => {
  if (!dateIsoString) return '';
  return formatInTimeZone(dateIsoString, timeZone, 'yyyy-MM-dd');
};
